import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { Episode } from '../types/Episode';

interface EpisodeCardProps {
  episode: Episode;
  onTagClick?: (tag: string) => void; // Optional callback for interactive tags
}

/**
 * Reusable Episode Card Component
 *
 * Displays episode information with thumbnail, title, excerpt, metadata, and tags.
 * Used on both HomePage (featured episodes) and EpisodesPage (all episodes).
 */
export const EpisodeCard: React.FC<EpisodeCardProps> = ({ episode, onTagClick }) => {
  return (
    <article className="episode-card group">
      <div className="flex flex-col h-full">
        {/* Episode Thumbnail */}
        {episode.thumbnail && (
          <Link
            to={`/episodes/${episode.slug}`}
            className="block mb-3 -mt-6 -mx-6 overflow-hidden rounded-t-lg"
          >
            <img
              src={episode.thumbnail}
              alt={`${episode.title} thumbnail`}
              className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </Link>
        )}

        {/* Episode Badge with Number */}
        <div className="flex items-center gap-2 mb-2">
          <span className="badge">EPISODE #{episode.episode}</span>
          {episode.featured && (
            <span className="badge badge-featured">FEATURED</span>
          )}
        </div>

        {/* Episode Title */}
        <Link to={`/episodes/${episode.slug}`}>
          <h3 className="text-lg font-bold text-gray-100 mb-2 hover:text-red-500 transition-colors cursor-pointer">
            {episode.title}
          </h3>
        </Link>

        {/* Episode Excerpt */}
        <p className="text-sm text-gray-400 mb-3 flex-1 leading-relaxed">
          {episode.excerpt}
        </p>

        {/* Metadata: Reading Time & Date */}
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
          <time className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {episode.readingTime} min read
          </time>
          <span>•</span>
          <span>
            {new Date(episode.date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </span>
        </div>

        {/* Tags (show first 3) */}
        <div className="flex flex-wrap gap-2 mb-4">
          {episode.tags.slice(0, 3).map((tag) =>
            onTagClick ? (
              <button
                key={tag}
                onClick={() => onTagClick(tag)}
                className="tag hover:bg-gray-700 hover:text-gray-200 transition-colors cursor-pointer"
              >
                {tag}
              </button>
            ) : (
              <span key={tag} className="tag">
                {tag}
              </span>
            )
          )}
          {episode.tags.length > 3 && (
            <span className="tag">+{episode.tags.length - 3}</span>
          )}
        </div>

        {/* Read Episode Link */}
        <Link
          to={`/episodes/${episode.slug}`}
          className="inline-flex items-center gap-2 text-red-500 hover:text-red-400 font-medium transition-colors group"
        >
          Read Episode
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </article>
  );
};
